// CardDestaque.js — Card ÚNICO de livro do site (grades, sliders e destaques).
//
// Histórico das duas correções que este arquivo concentra:
//
//  1. PROPORÇÃO DA CAPA. Antes a capa era `w-full h-auto`, ou seja, cada livro
//     tinha uma altura diferente e título/autor/preço não alinhavam entre os
//     cards da mesma linha — a grade ficava serrilhada. Agora a capa vive num
//     contêiner `aspect-[3/4]` com `object-cover`, o mesmo padrão que o
//     `CompreJunto` já usava. Todas as linhas ficam retas.
//
//  2. UM CARD SÓ. Existiam `CardProduto` e `CardDestaque` fazendo a mesma coisa
//     com aparências diferentes: um mostrava "Adicionar ao Carrinho" sempre, o
//     outro só no hover (um botão redondo flutuante). Na home isso aparecia como
//     duas seções vizinhas com comportamentos distintos. Ficou um componente só,
//     com o CTA SEMPRE visível — o botão que só aparecia no hover era invisível
//     em celular, onde não existe hover e está a maior parte do tráfego.
//
//  3. ACABAMENTO "SOMBRA DURA". Contorno grosso + sombra sólida deslocada, que
//     cresce e levanta no hover. Veio do Creative Pricing (21st.dev), de onde
//     entrou SÓ o acabamento: a rotação dos cards ficou de fora de propósito,
//     porque inclinar cada card desalinha título e preço entre vizinhos — que
//     é exatamente o defeito nº 1 corrigido no item 1 acima. A fonte manuscrita
//     também ficou de fora: não existe no sistema de design do site.
//     O contorno NÃO usa branco: usa `insigne-navy-light`, um azul claro da
//     própria família navy. Branco chapado em contorno + sombra + botão deixava
//     o card duro e apagava a marca; o azul claro dá o mesmo contraste alto do
//     BORDA e SOMBRA usam `--c-sombra`, que inverte com o tema: no escuro
//     precisam ser mais escuras que o fundo, senão viram brilho e não contorno;
//     no claro viram um lilás claro, que sobre o bege lê como folha empilhada.
//     O card fica sendo a superfície CLARA dentro de um contorno escuro — é daí
//     que vem o recorte, e não de uma linha clara em volta.
//     Os BOTÕES seguem regra própria, e cada um usa a cor da SUA sombra como
//     contorno: o carrinho é laranja com contorno e sombra no laranja ESCURO
//     (`--c-orange-dark`), o que o deixa como um objeto laranja inteiro, em
//     relevo — e não laranja contornado por outra cor; o coração é de vidro,
//     com contorno e sombra em branco translúcido, porque ele fica em cima da
//     capa. O selo DESTAQUE ficou com o âmbar claro porque é rótulo, não
//     botão: ali o contraste claro ajuda a ler o texto pequeno sobre o laranja.
import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import TextoTruncado from "../../utils/TruncarTexto";
import NotaLivro from "../paginaProduto/NotaLivro";
import PrecoComDesconto from "../../ui/PrecoComDesconto";
import TarjaDesconto from "../../ui/TarjaDesconto";
import { fmtPreco as formatarPreco, percentualOff } from "../../../utils/precoLivro";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";

// `variant` é mantido por compatibilidade com as chamadas existentes; hoje o
// card é o mesmo em todo lugar (era exatamente esse o problema).
// `onQuickView`: quando informado (só o catálogo informa), a capa ganha um
// botão de espiada rápida. Sem ele o card fica exatamente como antes.
// `selo`: fita no canto superior esquerdo (ex.: "Destaque"). Só quem escolhe
// os livros — a curadoria do CMS — sabe o que merece selo, então quem passa é
// a seção, não o card.
const CardDestaque = ({ livro, variant, onQuickView, selo }) => {
  // O selo do presente entra QUANDO NINGUEM PEDIU OUTRO. `selo` e a fita da
  // curadoria ("Destaque"), escolhida a dedo no CMS; ela vence, porque uma
  // fita so cabe naquele canto e quem selecionou o livro tinha um motivo.
  //
  // A flag vem pronta do servidor (`ebook_de_brinde` em GET /ebook), e nao
  // e deduzida aqui: a mesma condicao decide a promessa na pagina do
  // produto e a liberacao da leitura no backend. Card prometendo o que a
  // pagina do livro nao confirma seria pior do que card sem selo.
  //
  // A LIBERAÇÃO VENCE O PRESENTE. "+ E-BOOK" diz "comprando o impresso, a
  // leitura vem junto"; "GRÁTIS" diz que não precisa comprar nada. Um título
  // pode ter as duas coisas verdadeiras ao mesmo tempo, e aí a segunda torna a
  // primeira irrelevante — quem já pode baixar não precisa do brinde.
  const copiaGratis = livro?.copia_gratis_ativa === true;
  const seloBase = selo || (copiaGratis ? "GRÁTIS" : livro?.ebook_de_brinde ? "+ E-BOOK" : null);
  // O selo da liberação é VERDE, e não o laranja dos outros: verde é a cor que
  // o site inteiro usa para "você paga menos" (o selo de desconto, a economia
  // no cartão de preço). Grátis é o extremo dessa mesma escala.
  const seloVerde = !selo && copiaGratis;
  const { id, titulo, image, preco, autores, quantidade, frete } = livro;

  // O PREÇO QUE ESTE CARD USA É O DO SERVIDOR, com o desconto dos destaques já
  // aplicado (`preco_com_desconto`, de App\Services\PrecoService).
  //
  // Isto NÃO é cosmético. O card ADICIONA AO CARRINHO, e o checkout confere o
  // valor recebido contra o preço que o servidor calcula. Um card que mostrasse
  // o desconto e mandasse o preço cheio (ou o contrário) faria o pedido ser
  // recusado com "o preço de um dos livros não confere" — que foi exatamente o
  // que a antiga Promoção da Semana fazia com todo livro que ela anunciava.
  const isValid = (p) => p && !isNaN(parseFloat(p)) && parseFloat(p) > 0;
  const precoCheio = isValid(preco) ? parseFloat(preco) : null;
  const precoDesc = isValid(livro?.preco_com_desconto) ? parseFloat(livro.preco_com_desconto) : null;
  const temDesconto = precoCheio != null && precoDesc != null && precoDesc < precoCheio;
  const precoVenda = temDesconto ? precoDesc : precoCheio;

  // ===== A FITA E O DESCONTO DEIXARAM DE DISPUTAR O MESMO CANTO =====
  //
  // A fita mostrava "10% OFF" quando havia desconto, e a curadoria só
  // aparecia em quem NÃO estava em promoção. Numa fileira de cinco cards isso
  // produzia quatro fitas "10% OFF" e uma "DESTAQUE" — dois rótulos que dizem
  // coisas diferentes, ocupando o mesmo lugar, e a linha parecendo desalinhada
  // sem motivo visível.
  //
  // Agora cada fato tem o seu lugar: a FITA diz que a editora escolheu o livro,
  // e o SELO VERDE ao lado do preço diz que ele está mais barato hoje. Um livro
  // pode ser as duas coisas, e agora consegue mostrar as duas.
  const seloExibido = seloBase;
  const API_URL = process.env.REACT_APP_API;
  const main = process.env.REACT_APP_MAIN;
  const { adicionarItem, toggleDesejo, estaNaLista } = useContext(CarrinhoContext);
  const { openModal } = useModal();
  const isFav = estaNaLista ? estaNaLista(id) : false;

  // Controle de hover via React (compatível com touch + mouse)
  const [isHover, setIsHover] = useState(false);
  // Enquanto a capa não chega, o poço fica com um brilho suave em vez de vazio
  const [capaCarregada, setCapaCarregada] = useState(false);

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // A nota vai junto para a "Minha Lista" poder mostrar as estrelas sem
    // fazer uma requisição por item salvo. Itens salvos ANTES desta mudança
    // não têm os campos — lá a linha de estrelas simplesmente não aparece.
    if (toggleDesejo) {
      toggleDesejo({
        id, titulo, image, preco: precoVenda ?? preco,
        ...(temDesconto ? { preco_original: precoCheio } : {}),
        autores, formato: "ebook",
        avaliacoes_avg_nota: livro.avaliacoes_avg_nota,
        avaliacoes_count: livro.avaliacoes_count,
      });
    }
  };

  const precoValido = precoVenda != null;

  // ===== O PREÇO DO IMPRESSO NO CARD =====
  //
  // O catálogo já recebia `preco_impresso` e só desenhava o e-book — quem queria
  // o livro de papel tinha de abrir a página de cada título para descobrir
  // quanto custava. A página do livro sempre mostrou os dois; o card, não.
  //
  // FORA quando o impresso é vendido em loja parceira (`redirecionar_fisico`):
  // o preço de lá não é nosso para anunciar, e a página do livro já trata esse
  // caso com "Ver na loja parceira". O card não pode prometer um valor que a
  // página seguinte não confirma.
  const precoFisicoNum = parseFloat(livro?.preco_impresso);
  const fisicoExterno = !!(livro?.redirecionar_fisico && String(livro?.link_fisico || "").trim());
  const temFisico = !isNaN(precoFisicoNum) && precoFisicoNum > 0 && !fisicoExterno;

  // Suporta autores em objeto/string e IGNORA valores nulos
  // (alguns livros têm autores: [null], o que quebrava o card inteiro).
  const primeiroAutor = Array.isArray(autores) ? autores.find((a) => a != null) : null;
  const autorNome = primeiroAutor
    ? (typeof primeiroAutor === "object" ? primeiroAutor.nome : primeiroAutor)
    : null;

  const scrollUp = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Adiciona ao carrinho (impede o link de capturar o clique no botão)
  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!precoValido) return;
      // `formato` do item = QUAL VERSÃO está sendo comprada, e todo card vende
      // o digital (usa `livro.preco`; o impresso é `preco_impresso`, escolhido
      // só no PrecoCard). NUNCA repassar `livro.formato` aqui: ali o campo é o
      // tamanho do impresso ("16x23 (160x230)") e foi o que quebrou o checkout
      // em 20/08/2026. O CarrinhoContext também saneia, mas a intenção fica
      // explícita na origem.
      // A nota e o autor viajam junto com o item para o carrinho poder
      // mostrá-los sem uma requisição por linha. Quem já tinha itens no
      // carrinho antes disto não tem os campos, e lá a linha de estrelas
      // simplesmente não aparece (ver a guarda em CarrinhoPage.js).
    const item = {
      id, titulo, preco: precoVenda,
      // O "de" acompanha o item para o carrinho poder risca-lo.
      ...(temDesconto ? { preco_original: precoCheio } : {}),
      image, autores, formato: "ebook",
      quantidade: quantidade || 1,
      avaliacoes_avg_nota: livro.avaliacoes_avg_nota,
      avaliacoes_count: livro.avaliacoes_count,
    };
    if (frete) item.frete = frete;
    adicionarItem(item);
    openModal(item);
  };

  return (
    <Link
      to={main + "livros/" + id}
      onClick={scrollUp}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onFocus={() => setIsHover(true)}
      onBlur={() => setIsHover(false)}
      className={
        "card-livro group/card block relative h-full rounded-card p-3 pb-6 border-2 border-insigne-sombra " +
        "bg-insigne-navy-soft transition-[transform,box-shadow] duration-200 will-change-transform " +
        (isHover ? "-translate-x-[3px] -translate-y-[3px]" : "")
      }
      style={{
        // Sombra SÓLIDA e deslocada (sem blur) — a assinatura do estilo. Cresce
        // no hover enquanto o card anda o mesmo tanto na diagonal oposta, o que
        // dá a impressão de o card se levantar da página.
        boxShadow: `${isHover ? 7 : 4}px ${isHover ? 7 : 4}px 0 0 rgb(var(--c-sombra))`,
      }}
    >
      {/* ===== AS DUAS QUINAS DE CIMA (11/09/2026) =====
          À ESQUERDA, o desconto: a tarja verde inclinada, a MESMA da Loja de
          Créditos e dos pacotes do Clube (ui/TarjaDesconto). Ela era um selo
          pequeno encostado no preço, e a pedido do dono passou a ocupar a quina
          — que é onde o olho já procura promoção em toda a loja.

          À DIREITA, a curadoria ("Destaque") ou o presente ("+ E-BOOK"), que
          antes moravam na esquerda. Os dois dizem coisas diferentes e agora têm
          cada um a sua quina, em vez de disputar a mesma.

          A inclinação vem do id do livro: varia de card para card na fileira e
          é SEMPRE a mesma para o mesmo livro — sorteada a cada renderização,
          ela mudaria sozinha a cada passada do mouse. */}
      {temDesconto && <TarjaDesconto pct={percentualOff(livro)} indice={Number(id) || 0} />}

      {seloExibido && (
        <span
          className={
            "absolute -top-2.5 right-3 z-20 rounded-selo border-2 px-2.5 py-0.5 " +
            "text-[10px] font-bold uppercase tracking-wide " +
            (seloVerde
              ? "border-insigne-promo bg-insigne-promo text-ink-fixed"
              : "border-insigne-amber bg-insigne-orange text-paper")
          }
        >
          {seloExibido}
        </span>
      )}
      {/* Botão de favoritar — mesma FORMA do botão de carrinho (cantos
          arredondados, contorno grosso, sombra sólida que cresce no hover),
          mas em vidro em vez de sólido: ele fica em cima da capa, e opaco
          taparia o livro.
          Contorno e sombra são a MESMA cor — branco a 45%, subindo a 60% no
          hover. Branco FIXO, não o token temável: isto cai sobre a capa do
          livro, que pode ter qualquer cor e não muda com o tema; e translúcido
          porque branco chapado apagaria um pedaço da capa.
          (O contorno era laranja-escuro. Laranja escuro em volta de um botão
          de vidro não pertencia nem ao botão nem à capa — ficava uma terceira
          cor solta em cima do livro.)
          Favoritado → coração vermelho cheio. */}
      <button
        onClick={handleFav}
        aria-label={isFav ? "Remover da Minha Lista" : "Salvar na Minha Lista"}
        aria-pressed={isFav}
        title={isFav ? "Remover da Minha Lista" : "Salvar na Minha Lista"}
        className="card-acao-hover absolute top-5 right-5 z-20 inline-flex items-center justify-center
                   w-11 h-11 rounded-controle border-2 border-paper/45
                   bg-paper/30 backdrop-blur-sm
                   shadow-[3px_3px_0_0_rgb(255_255_255_/_0.45)]
                   transition-[box-shadow,background-color,border-color] duration-150
                   hover:bg-paper/55 hover:border-paper/60
                   hover:shadow-[5px_5px_0_0_rgb(255_255_255_/_0.6)]"
      >
        <i
          className={
            "bi text-base " +
            // Sobre a base branca o coração vazio precisa ser ESCURO; em
            // branco ele sumiria dentro do próprio botão.
            (isFav ? "bi-heart-fill text-red-500" : "bi-heart text-ink-fixed")
          }
        />
      </button>

      {/* ===== Capa =====
          O contêiner é quem define a proporção (3:4, padrão de capa de livro).
          A imagem preenche com object-cover, então capas de qualquer tamanho
          ocupam exatamente o mesmo espaço e a grade fica alinhada. */}
      <div className="relative overflow-hidden rounded-card border-2 border-insigne-sombra bg-insigne-navy-deep aspect-[3/4]">
        {!capaCarregada && (
          <div className="absolute inset-0 animate-pulse bg-insigne-navy-mid/60" />
        )}
        <img
          src={`${API_URL}public/${image}`}
          alt={titulo}
          loading="lazy"
          onLoad={() => setCapaCarregada(true)}
          onError={() => setCapaCarregada(true)}
          className={
            "relative w-full h-full object-cover transition-transform duration-500 ease-out " +
            (isHover ? "scale-[1.05]" : "scale-100") +
            (capaCarregada ? " opacity-100" : " opacity-0")
          }
        />

        {/* Espiada rápida — some no toque (onde não há hover) para não tapar a
            capa; ali o toque na capa já abre a página do livro. */}
        {onQuickView && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickView(livro); }}
            aria-label={`Espiada rápida em ${titulo}`}
            style={{ opacity: isHover ? 1 : 0, pointerEvents: isHover ? "auto" : "none" }}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 hidden md:flex items-center gap-1.5
                       rounded-controle bg-black/70 backdrop-blur-sm border border-line px-3 py-1.5
                       text-xs font-semibold text-paper transition-opacity duration-200 hover:bg-black/85"
          >
            <i className="bi bi-eye" /> Espiada rápida
          </button>
        )}
      </div>

      {/* ===== Título + autor + preço ===== */}
      <div className="mt-3.5 px-1">
        {/* Altura FIXA de duas linhas (não `min-h`): com altura mínima, um
            título de uma linha e outro de duas empurravam preço e botão para
            alturas diferentes, e a linha da grade voltava a ficar torta. */}
        <h3 className="text-white font-bold leading-snug text-sm md:text-base
                       h-10 md:h-11 line-clamp-2 overflow-hidden">
          <TextoTruncado texto={titulo} limite={30} />
        </h3>
        <p className="text-white/55 text-xs mt-1 h-4 truncate">{autorNome || ""}</p>

        {/* Nota do livro. Vem embutida na resposta do livro (avaliacoes_avg_nota
            / avaliacoes_count), NUNCA de uma requisição por card: a grade do
            catálogo tem 198 títulos e a API roda num MySQL com limite de
            conexões. Altura fixa para a linha da grade continuar reta quando um
            card tem nota e o vizinho não. */}
        <div className="h-5 flex items-center mt-1">
          <NotaLivro livro={livro} size="text-[11px]" compacto />
        </div>

        {/* ===== O PREÇO: riscado em cima, selo à direita =====
            O desenho é o de PrecoComDesconto, o MESMO de toda vitrine que
            mostra livro — ver o cabeçalho de lá. A linha do riscado fica
            reservada mesmo sem desconto, para o preço de todos os cards de
            uma linha da grade cair na mesma altura.

            O selo é verde, e não o laranja da marca: laranja no site inteiro
            significa "olhe aqui", e a fita, o botão de carrinho e o preço já
            diziam isso ao mesmo tempo no mesmo card. */}
        {copiaGratis ? (
          // ===== O PREÇO QUE NÃO É UM PREÇO =====
          //
          // Desenhado com as MESMAS DUAS LINHAS do PrecoComDesconto (a linha
          // `h-4` do rótulo e a linha do número grande), porque numa grade este
          // card divide a fileira com outros que têm preço: um desenho mais
          // curto aqui deixaria a linha da grade torta, que é o defeito nº 1 do
          // cabeçalho deste arquivo.
          //
          // Sem "R$ 0,00": zero é um preço, e preço zero lê como cadastro pela
          // metade — o cliente desconfia do livro em vez de abri-lo.
          <div className="mt-1.5">
            <p className="flex h-4 items-baseline text-sm leading-4">
              <span className="text-[10px] font-bold uppercase tracking-wide text-white/55">
                E-book
              </span>
            </p>
            <p className="flex items-center">
              <span className="text-xl md:text-2xl font-bold text-insigne-promo">Grátis</span>
            </p>
          </div>
        ) : precoValido ? (
          <PrecoComDesconto
            livro={livro}
            // O desconto já está na quina do card, inclinado.
            mostrarSelo={false}
            // O rótulo só aparece quando há DOIS formatos: num livro só
            // digital, escrever "E-book" em cima do preço é ruído.
            rotulo={temFisico ? "E-book" : ""}
            className="mt-1.5 text-white/90"
          />
        ) : temFisico ? (
          // Livro SÓ impresso: o preço de papel vira o preço principal, na
          // mesma altura e peso do e-book dos vizinhos. Antes aparecia só
          // "Disponível em físico", que diz que existe e esconde quanto custa.
          <div className="mt-1.5 text-white/90">
            <p className="h-4 text-[10px] font-bold uppercase leading-4 tracking-wide text-white/55">
              Físico
            </p>
            <p className="flex items-center">
              <span className="text-xs align-top">R$</span>{" "}
              <span className="text-xl md:text-2xl font-bold">{formatarPreco(precoFisicoNum)}</span>
            </p>
          </div>
        ) : (
          // `pt-[19px]` = os 16px da linha do riscado que os vizinhos reservam
          // mais os 3px de antes: o texto fica na altura do preço deles.
          <p className="text-sm text-white/50 mt-1.5 pt-[19px] pb-[3px]">Disponível em físico</p>
        )}

        {/* O IMPRESSO, logo abaixo do e-book, quando o livro vende os dois.
            Altura RESERVADA mesmo sem impresso: numa grade, o card que tem a
            linha e o vizinho que não tem ficariam com alturas diferentes, e o
            botão de carrinho, que é pendurado na borda, sairia torto. */}
        <p className="mt-1 h-4 text-[11px] leading-4 text-white/55" aria-hidden={!(precoValido && temFisico)}>
          {precoValido && temFisico && (
            <>
              <span className="font-bold uppercase tracking-wide text-[10px]">Físico</span>{" "}
              <span className="text-white/80">R$ {formatarPreco(precoFisicoNum)}</span>
            </>
          )}
        </p>

        {/* CTA e favoritar só aparecem com o cursor no card (em aparelho de
            toque continuam visíveis — ver `.card-acao-hover` no App.css). */}
      </div>

      {/* Botão de carrinho pendurado na borda inferior: metade dentro, metade
          fora. Fica FORA do fluxo (absolute), então não ocupa altura e não
          desalinha os cards da linha — mas exige gap vertical na grade para a
          metade que vaza não encostar no card de baixo. */}
      {/* SEM BOTÃO DE CARRINHO no título liberado: não há o que adicionar, e
          o card inteiro já é o link para a página onde se lê e se baixa.
          (O impresso desse mesmo título continua à venda — só que de dentro da
          página do livro, que é o único lugar do site que vende papel.) */}
      {precoValido && !copiaGratis && (
        <button
          onClick={handleAdd}
          aria-label={`Adicionar ${titulo} ao carrinho`}
          title="Adicionar ao carrinho"
          className="card-acao-hover group/cart absolute left-1/2 bottom-0 z-20 -translate-x-1/2 translate-y-1/2
                     inline-flex items-center justify-center w-14 h-11 rounded-controle
                     border-2 border-insigne-orange-dark bg-insigne-orange text-paper
                     shadow-[3px_3px_0_0_rgb(var(--c-orange-dark))]
                     transition-[box-shadow,background-color] duration-150
                     hover:bg-insigne-orange-alt hover:shadow-[5px_5px_0_0_rgb(var(--c-orange-dark))]"
        >
          {/* Carrinho vazio em repouso, cheio com o cursor no botão: o próprio
              ícone antecipa o que o clique vai fazer. Os dois ficam no DOM e a
              troca é CSS — sem estado em React para um hover. */}
          <i className="bi bi-cart text-base group-hover/cart:hidden" />
          <i className="bi bi-cart-fill text-base hidden group-hover/cart:inline" />
        </button>
      )}
    </Link>
  );
};

export default CardDestaque;
